import React, { useEffect, useRef } from 'react'
import { Container, Nav, Navbar } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMediaQuery } from 'react-responsive'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

import { isTabletOrMobileQuery } from '../../utils'
import ToastContainer from './ToastContainer'
import TestModeIndicator from './TestModeIndicator'
import { useDispatch } from 'react-redux'
import { clearToasts } from '../../store/misc/misc'

export type NavigationButton = {
  text: string
  handler: () => void | Promise<void>
  icon?: IconProp
  verifyClass?: string
}

export type StageNavigationProps = {
  forward?: NavigationButton
  backward?: NavigationButton
  hasRegistration?: boolean
}

type LocalStageNavigationProps = {
  scrollRef: React.RefObject<HTMLDivElement>
} & StageNavigationProps

const StageNavigation = (props: StageNavigationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  let timeoutId: NodeJS.Timeout | null = null;

  const scrollHandler = () => {
  const backToTopElement = ref.current;
  if (!backToTopElement) return;

  if (window.scrollY >= 500) {
    if (backToTopElement.style.visibility !== 'visible') {
      backToTopElement.style.visibility = 'visible';
      backToTopElement.classList.remove('animated', 'fadeOut', 'faster');
      backToTopElement.classList.add('animated', 'slideInRight', 'faster');
    }
  } else {
    if (backToTopElement.style.visibility !== 'hidden') {
      backToTopElement.classList.remove('animated', 'slideInRight', 'faster');
      backToTopElement.classList.add('animated', 'fadeOut', 'faster');

      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (backToTopElement) {
          backToTopElement.style.visibility = 'hidden';
        }
      }, 500);
    }
  }
};

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler);

      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  if (useMediaQuery(isTabletOrMobileQuery)) {
    return <MobileStageNavigation {...props} scrollRef={ref} />
  } else {
    return <DesktopStageNavigation {...props} scrollRef={ref} />
  }
}

export default StageNavigation

const MobileStageNavigation = (props: LocalStageNavigationProps) => {
  const { forward, backward, scrollRef } = props
  const dispatch = useDispatch()
  return (
    <>
      <ToastContainer />
      <div
        className='nav-item back-to-top-container'
        ref={scrollRef}
        role='navigation'
        aria-label='Enthält Navigationselement um zu den Seitenanfang zu gelangen'
      >
        <Nav.Link
          className='btn btn-primary back-to-top-btn d-flex extra-rounded'
          onClick={() => window.scrollTo(0, 0)}
          role='navigation'
        >
          <FontAwesomeIcon icon='chevron-up' aria-label='Zum Seitenanfang' />
        </Nav.Link>
      </div>
      <Navbar
        expand='lg'
        variant='dark'
        bg='dark'
        fixed='bottom'
        aria-label='Unterer Navigationsbalken'
      >
        <div className='flex-column w-100'>
          <TestModeIndicator />
          {forward && (
            <Nav.Item>
              <Nav.Link
                className={
                  'btn d-block ' +
                  `${
                    forward.verifyClass
                      ? 'btn-success'
                      : 'btn-primary dark-primary-background'
                  }`
                }
                onClick={() => {
                  forward.handler()
                  dispatch(clearToasts())
                }}
              >
                {forward.text}
                <FontAwesomeIcon
                  icon={
                    forward.icon || 'chevron-right'
                  }
                  role='presentation'
                  className='ml-2'
                />
              </Nav.Link>
            </Nav.Item>
          )}
          {backward && (
            <Nav.Item>
              <Nav.Link
                className='btn btn-light mt-3 d-block'
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  backward.handler()
                  dispatch(clearToasts())
                }}
              >
                <FontAwesomeIcon
                  icon={
                    backward.icon || 'chevron-left'
                  }
                  role='presentation'
                />{' '}
                {backward.text}
              </Nav.Link>
            </Nav.Item>
          )}
        </div>
      </Navbar>
    </>
  )
}

const DesktopStageNavigation = (props: LocalStageNavigationProps) => {
  const { forward, backward, scrollRef } = props
  const dispatch = useDispatch()
  return (
    <>
      <ToastContainer />
      <div
        className='nav-item back-to-top-container'
        ref={scrollRef}
        role='navigation'
        aria-label='Enthält Navigationselement um zu den Seitenanfang zu gelangen'
      >
        <Nav.Link
          className='btn btn-primary back-to-top-btn d-flex extra-rounded'
          onClick={() => window.scrollTo(0, 0)}
          role='navigation'
          aria-label='Zum Seitenanfang'
        >
          <FontAwesomeIcon icon='chevron-up' role='presentation' />
        </Nav.Link>
      </div>
      <Navbar
        expand='lg'
        variant='dark'
        bg='dark'
        fixed='bottom'
        aria-label='Unterer Navigationsbalken'
      >
        <Container className='px-5'>
          {backward && (
            <Nav.Item>
              <Nav.Link
                className='btn btn-light'
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  dispatch(clearToasts())
                  backward.handler()
                }}
              >
                <FontAwesomeIcon
                  icon={
                    backward.icon || 'chevron-left'
                  }
                  role='presentation'
                />{' '}
                {backward.text}
              </Nav.Link>
            </Nav.Item>
          )}
          <TestModeIndicator />
          {forward && (
            <Nav.Item>
              <Nav.Link
                className={
                  'btn ' +
                  `${
                    forward.verifyClass
                      ? 'btn-success'
                      : 'btn-primary dark-primary-background'
                  }`
                }
                onClick={() => {
                  dispatch(clearToasts())
                  forward.handler()
                }}
              >
                {forward.text}
                <FontAwesomeIcon
                  icon={
                    forward.icon || 'chevron-right'
                  }
                  role='presentation'
                  className='ml-2'
                />
              </Nav.Link>
            </Nav.Item>
          )}
        </Container>
      </Navbar>
    </>
  )
}
